<script setup lang="ts">
import { onMounted, onUnmounted, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
// eslint-disable-next-line import/no-cycle
import Header from '@/components/Header.vue';
import AppFooter from '@/components/AppFooter.vue';
import ProductTourHost from '@/components/onboarding/ProductTourHost.vue';
import CreditorAccessBanner from '@/components/superadmin/CreditorAccessBanner.vue';
import { useAccountStore } from '@/stores/account';
import { useAuthStore } from '@/stores/auth';
import { useNotificationsStore } from '@/stores/notifications';
import { useSettingsStore } from '@/stores/settings';
import { useSuperAdminCreditorAccessStore } from '@/stores/superAdminCreditorAccess';

const router = useRouter();
const accountStore = useAccountStore();
const authStore = useAuthStore();
const notificationsStore = useNotificationsStore();
const settingsStore = useSettingsStore();
const creditorAccessStore = useSuperAdminCreditorAccessStore();
const { user } = storeToRefs(authStore);
const { creditorName, isActive: isCreditorAccessActive } = storeToRefs(creditorAccessStore);

onMounted(() => {
    accountStore.fetchAccountSettings();
    accountStore.fetchDevices();
    settingsStore.fetchCompanySettings();
});

let stopWatching: (() => void) | undefined;

// `let` avoids the temporal dead zone when this immediate watcher stops itself.
// eslint-disable-next-line prefer-const
stopWatching = watch(user, async (newUser) => {
    if (!newUser?.id) return;

    stopWatching?.();
    await notificationsStore.fetchUnreadCount();
    notificationsStore.listenForNewNotifications(newUser.id);
}, { immediate: true });

onUnmounted(() => {
    stopWatching?.();
    if (authStore.user?.id) {
        notificationsStore.stopListening(authStore.user.id);
    }
});

async function handleExitCreditorAccess(): Promise<void> {
    await creditorAccessStore.exitAccess();
    await router.push({ name: 'superadmin-creditors' });
}
</script>

<template>
    <div
        class="default-layout"
        :class="{ 'default-layout--with-creditor-access': isCreditorAccessActive }"
    >
        <CreditorAccessBanner
            v-if="isCreditorAccessActive"
            :creditor-name="creditorName"
            @exit="handleExitCreditorAccess"
        />
        <div class="default-layout__shell">
            <Header />
            <main class="default-layout__main">
                <router-view />
            </main>
            <AppFooter />
        </div>
        <ProductTourHost />
    </div>
</template>

<style lang="scss" scoped>
.default-layout {
    min-height: 100vh;

    &__shell {
        display: flex;
        flex-direction: column;
        gap: 40px;
        min-height: 100vh;
        padding: 0 $page-padding-x;
    }

    &__main {
        flex: 1;
    }

    &--with-creditor-access {
        .default-layout__shell {
            min-height: calc(100vh - 59px);
        }
    }
}
</style>
